import { itemsApi } from "#/api/itemsApi";
import { rfidTagApi } from "#/api/rfidTagApi";
import { showSuccessNotification } from "#/components/notifications";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { Item } from "#/types/items";
import type { RfidTag } from "#/types/rfidTag";
import { logger } from "#/utils/logger";
import { Button, Modal, Select, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface LinkRfidTagToItemModalProps {
  opened: boolean;
  onClose: () => void;
  rfidTag: RfidTag | undefined;
  onSuccess: () => void;
}

/** How many tag-less items the picker shows at once; the search narrows it. */
const PICKER_PAGE_SIZE = 50;

/** "SAWA-N2-0003 · Nitrogen Cylinder 50 L · Sawa Depot Nord", empty parts left out. */
const itemOptionLabel = (item: Item): string =>
  [
    item.external_id?.trim() || `Item #${item.id}`,
    item.product?.name?.trim() ?? "",
    item.site?.name?.trim() || item.location?.trim() || "",
  ]
    .filter((part) => part.length > 0)
    .join(" · ");

/**
 * Links one loose RFID tag to an existing item that has no tag yet. The picker
 * only ever lists tag-less items (backend filter `has_rfid=false`), so a tag
 * cannot be attached to an item that is already tagged from here.
 */
const LinkRfidTagToItemModal = ({
  opened,
  onClose,
  rfidTag,
  onSuccess,
}: LinkRfidTagToItemModalProps) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const { data: candidates = [], isFetching } = useQuery({
    queryKey: ["rfid-link-item-candidates", debouncedSearch],
    queryFn: async () => {
      const response = await itemsApi.getAll({
        page: 1,
        limit: PICKER_PAGE_SIZE,
        filterBy: "has_rfid=false",
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      return response.data?.data ?? [];
    },
    enabled: opened,
    staleTime: 0,
  });

  const linkMutation = useMutation({
    mutationFn: async ({ tagId, itemId }: { tagId: number; itemId: number }) =>
      rfidTagApi.update(tagId, { item_id: itemId }),
    onSuccess: (_data, { itemId }) => {
      showSuccessNotification({
        message: t("rfidTags.linkItem.success", { id: itemId }),
      });
      handleClose();
      onSuccess();
    },
    onError: (error) => {
      logger.error("LinkRfidTagToItemModal link error:", error);
    },
  });

  const handleClose = () => {
    setSelectedItemId(null);
    setSearch("");
    onClose();
  };

  const handleLink = () => {
    if (!rfidTag || !selectedItemId) return;
    const itemId = Number(selectedItemId);
    if (!Number.isInteger(itemId) || itemId <= 0) return;
    linkMutation.mutate({ tagId: rfidTag.id, itemId });
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={t("rfidTags.linkItem.title")}
      closeOnClickOutside={false}
      size="lg"
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col gap-4">
        <Text size="sm">
          {t("rfidTags.linkItem.description", { rfid: "" })}{" "}
          <strong className="font-mono text-black">{rfidTag?.rfid ?? "--"}</strong>
        </Text>

        <Select
          label={t("rfidTags.linkItem.itemLabel")}
          placeholder={t("rfidTags.linkItem.itemPlaceholder")}
          withAsterisk
          searchable
          clearable
          searchValue={search}
          onSearchChange={setSearch}
          data={candidates.map((item) => ({
            value: String(item.id),
            label: itemOptionLabel(item),
          }))}
          value={selectedItemId}
          onChange={setSelectedItemId}
          nothingFoundMessage={
            isFetching ? t("rfidTags.linkItem.loading") : t("rfidTags.linkItem.noItems")
          }
          disabled={!canWrite() || linkMutation.isPending}
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            color="black"
            onClick={handleClose}
            disabled={linkMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              onClick={handleLink}
              loading={linkMutation.isPending}
              disabled={!selectedItemId || !rfidTag || !canWrite()}
            >
              {t("rfidTags.linkItem.linkButton")}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default LinkRfidTagToItemModal;
